import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import { Box, Divider, Typography, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import PriceDetailsForm from "./price-details/price-details-form";
import PropertyDetailsForm from "./property-details-form";
import CustomAutoComplete from "@/modules/common/components/input/dropdowns/auto-complete";
import useTrackTypesFilterOptions from "@/modules/common/hooks/lookups/track-types-filter-options.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import {
  ACCEPTED_TRACK_FILE_TYPES,
  addTrackDetailedSchema,
  mandationPerTrackType,
  PROPERTY_TRACKS,
} from "@/modules/property-tracks/utils";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType, UploadedFile } from "@/modules/common/types";
import {
  TrackDocument,
  TrackDocumentType,
} from "@/modules/property-tracks/types";
import { useAddTrackDetailed } from "@/modules/property-tracks/hooks/add-detailed-track.hook";
import { useCustomer } from "@/modules/clients/hooks/use-customer.hook";
import { useParams } from "react-router-dom";
import { FileUploadButton } from "@/modules/common/components/input/file-upload/file-upload";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import useTrackDocumentTypes from "@/modules/common/hooks/document-types.hook";
import { PriceRoles } from "@/modules/property-tracks/dtos";
import { enforceMandates, MandateFields } from "@/modules/common/utils/zod";

interface TrackTypeOption {
  ids?: number[];
  id?: number;
  name: string;
  leadTypeUid?: string;
  key?: string;
}

type AddTrackDetailedFormFields = z.infer<
  ReturnType<typeof addTrackDetailedSchema>
>;

const AddTrackDetailedForm = ({
  customerUId,
  modalOpen,
  setModalOpen,
  leadTypeUId,
}: {
  customerUId: string;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  leadTypeUId?: string;
}) => {
  const { client_id } = useParams<{ client_id: string }>();
  const [trackType, setTrackType] = useState<PROPERTY_TRACKS | undefined>();
  const [files, setFiles] = useState<TrackDocument[]>([]);
  const [mandates, setMandates] =
    useState<MandateFields<AddTrackDetailedFormFields>>();
  const theme = useTheme();

  const { data: client } = useCustomer({
    uid: client_id,
  });

  const mainPhone =
    client?.telephones.find((telephone) => telephone.default)?.telephone ||
    client?.telephones[0]?.telephone ||
    "";

  const addTrackDetailedForm = useForm<AddTrackDetailedFormFields>({
    resolver: zodResolver(
      addTrackDetailedSchema().superRefine((data, ctx) => {
        if (mandates) enforceMandates(data, mandates, ctx, []);
      }),
    ),
  });

  const {
    handleSubmit: handleSubmitAddTrackDetailed,
    reset: resetAddTrackDetailed,
    control: controlAddTrackDetailed,
    watch: watchTrackDetailedFormState,
    setValue: setValueTrackDetailedValues,
    trigger: triggerTrackDetailed,
  } = addTrackDetailedForm;

  const organizationUId = watchTrackDetailedFormState("organizationUId");
  const { data: trackTypes, isLoading: TrackTypesLoading } =
    useTrackTypesFilterOptions({ organizationUId });

  const filteredTrackTypes = useMemo(() => {
    if (!trackTypes) return [];
    const typedTrackTypes = trackTypes as unknown as TrackTypeOption[];

    if (!leadTypeUId) return typedTrackTypes;

    const matches = typedTrackTypes.filter(
      (type) => type.leadTypeUid === leadTypeUId,
    );

    return matches.length > 0 ? matches : typedTrackTypes;
  }, [trackTypes, leadTypeUId]);

  const onSubmitAddTrackDetailed: SubmitHandler<
    AddTrackDetailedFormFields
  > = async (data) => {
    submitAddTrack(data);
  };

  const trackTypeValue = watchTrackDetailedFormState("trackType");
  const selectedTrackType = useMemo(() => trackTypeValue, [trackTypeValue]);

  const filteredTrackTypesLength = filteredTrackTypes.length;
  const firstTrackType = filteredTrackTypes[0];
  const firstTrackTypeId = firstTrackType?.ids?.[0] || firstTrackType?.id;
  const firstTrackTypeName = firstTrackType?.name;

  useEffect(() => {
    if (filteredTrackTypesLength === 1 && firstTrackTypeId) {
      if (trackTypeValue?.value !== firstTrackTypeId) {
        setValueTrackDetailedValues("trackType", {
          label: firstTrackTypeName,
          value: firstTrackTypeId,
        });
      }
    }
  }, [
    filteredTrackTypesLength,
    firstTrackTypeId,
    firstTrackTypeName,
    trackTypeValue,
    setValueTrackDetailedValues,
  ]);

  const trackDocumentTypes = useTrackDocumentTypes(selectedTrackType?.value);

  useEffect(() => {
    setFiles([]);
    if (selectedTrackType?.label as PROPERTY_TRACKS) {
      const track = selectedTrackType?.label as PROPERTY_TRACKS;
      setTrackType(track);
      setMandates(mandationPerTrackType[track]);
    }
    setValueTrackDetailedValues("property.propertyPrice", undefined);
  }, [selectedTrackType, setValueTrackDetailedValues]);

  const mutationAddTrackDetailed = useAddTrackDetailed();

  const {
    filteredManagedOrganizations,
    renderOrganisationsActions,
    selectedOrganization,
  } = useOrganization();

  const submitAddTrack = (data: AddTrackDetailedFormFields) => {
    const organizationUId = data.organizationUId ?? selectedOrganization?.uid;

    if (!organizationUId) {
      showToast({
        type: ToastType.ERROR,
        message: "Please select an organization",
        toastId: TOAST_IDS.ADD_TRACK_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
      return;
    }
    let sendPropertyPrice: boolean = false;
    if (data.property.propertyPrice) {
      const { paymentType, ...rest } = data.property.propertyPrice;
      if (Object.keys(rest).length > 0) {
        sendPropertyPrice = true;
      }
    }
    mutationAddTrackDetailed.mutate(
      {
        data: {
          customerUId,
          customerTelephone: mainPhone,
          trackTypeId: data.trackType.value,
          organizationUId: organizationUId ?? "",
          leadTypeUId: leadTypeUId,
          property: {
            compoundId: data.property.compound?.value,
            propertyTypeId: data.property.propertyType?.value,
            unitId: data.property.unitId,
            description: data.property.description ?? undefined,
            amenityIds: [...(data.property.amenityIds?.values() ?? [])],
            numberOfBedrooms: data.property.numberOfBedrooms,
            numberOfBathrooms: data.property.numberOfBathrooms,
            unitArea: data.property.unitArea,
            phase: data.property.phase,
            landArea: data.property.landArea,
            gardenArea: data.property.gardenArea,
            roofArea: data.property.roofArea,
            ...(trackType !== PROPERTY_TRACKS.HOSPITALITY && {
              isUnderConstruction: data.property.isUnderConstruction,
            }),
            floorNumber: data.property.floorNumber,
            numberOfFloors: data.property.numberOfFloors,
            deliveryDate: data.property.readyToMove
              ? undefined
              : data.property.deliveryDate,
            readyToMove: data.property.readyToMove,
            lastInstallmentDate: data.property.lastInstallmentDate,
            finishing: data.property.finishing?.value,
            propertyPrice: sendPropertyPrice
              ? {
                  ...data.property.propertyPrice,
                  installmentFrequency:
                    data.property.propertyPrice?.installmentFrequency
                      ?.value,
                  priceRole: PriceRoles.ASKING,
                }
              : undefined,
            latitude: data.property.latitude,
            longitude: data.property.longitude,
            address: data.property.address,
          },
        },
        files: files,
      },
      {
        onSuccess: () => {
          handleCloseModal();

          showToast({
            type: ToastType.SUCCESS,
            message: `Property Track added successfully.`,
            toastId: TOAST_IDS.ADD_TRACK_SUCCESS,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.ADD_TRACK_ERROR,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
      },
    );
  };

  const handleFile = useCallback((newFile: TrackDocument) => {
    setFiles((prev) => [...prev, newFile]);
  }, []);

  const handleDeleteFile = useCallback(
    (fileToDelete: UploadedFile, documentType: TrackDocumentType) => {
      setFiles((prevFiles) => {
        const updatedFiles = prevFiles.filter(
          (file) =>
            file.file.name !== fileToDelete.name ||
            file.documentType !== documentType,
        );
        return updatedFiles.length > 0 ? updatedFiles : [];
      });
    },
    [],
  );

  const resetFormState = () => {
    resetAddTrackDetailed();
    setFiles([]);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    resetFormState();
  };

  return (
    <>
      <EditFieldModal
        open={modalOpen}
        setOpen={setModalOpen}
        header="Add Property"
        width={480}
        handleConfirm={handleSubmitAddTrackDetailed(onSubmitAddTrackDetailed)}
        handleCloseModal={handleCloseModal}
        isLoading={mutationAddTrackDetailed.isPending}
      >
        <Typography sx={theme.nawy.typography.english.variants.body.md.regular}>
          Unit details
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.md,
          }}
        >
          {renderOrganisationsActions && (
            <>
              <Controller
                name="organizationUId"
                control={controlAddTrackDetailed}
                render={({ field, fieldState }) => (
                  <CustomAutoComplete
                    required
                    placeholder="Select Organization"
                    options={
                      filteredManagedOrganizations?.map((org) => ({
                        label: org.organization?.name ?? "",
                        value: org.organization?.uid ?? "",
                      })) ?? []
                    }
                    label="Organization*"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    onChange={(value) => {
                      field.onChange(value);
                    }}
                  />
                )}
              />
            </>
          )}

          <Controller
            control={controlAddTrackDetailed}
            name="trackType"
            render={({ field, fieldState }) => {
              const trackTypeOptions =
                filteredTrackTypes?.map((type) => ({
                  label: type.name,
                  value: (type.ids?.[0] || type.id)?.toString() || "",
                })) || [];

              // Convert stored number value to string for component
              const displayValue = field.value
                ? {
                    label: field.value.label,
                    value: field.value.value?.toString() || "",
                  }
                : null;

              return (
                <CustomAutoComplete
                  value={displayValue}
                  options={trackTypeOptions}
                  required
                  loading={TrackTypesLoading}
                  placeholder="Property Track"
                  label="Property Track"
                  disabled={filteredTrackTypes.length === 1}
                  onChange={(value) => {
                    if (value && !Array.isArray(value)) {
                      // Convert string value back to number for schema
                      const newValue = {
                        label: value.label,
                        value: Number(value.value),
                      };
                      field.onChange(newValue);
                    } else {
                      field.onChange(null);
                    }
                    // Trigger validation after state update
                    setTimeout(() => {
                      triggerTrackDetailed("trackType");
                    }, 0);
                  }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                ></CustomAutoComplete>
              );
            }}
          ></Controller>

          <PropertyDetailsForm
            form={addTrackDetailedForm}
            namePrefix="property"
            trackTypeName={trackType}
          />
          {trackDocumentTypes.map((value) => (
            <FileUploadButton
              label={value}
              setFiles={(files) => {
                files.forEach((file) => {
                  handleFile({
                    documentType: value,
                    file: file,
                  } as TrackDocument);
                });
              }}
              acceptedFileTypes={ACCEPTED_TRACK_FILE_TYPES[value]}
              onDeleteFile={(file) => {
                handleDeleteFile(file, value);
              }}
              maxFiles={10}
            />
          ))}
          {trackType === PROPERTY_TRACKS.RESALE && (
            <>
              <Divider sx={{ my: theme.nawy.spacing.lg }} />
              <PriceDetailsForm
                form={addTrackDetailedForm}
                namePrefix="property.propertyPrice"
              />
            </>
          )}
        </Box>
      </EditFieldModal>
    </>
  );
};

export default AddTrackDetailedForm;
